import { useEffect, useRef } from "react";

import { useTheresiasEventBridge } from "./useTheresiasEventBridge";

const TAB_COUNT_KEY = "_zenvoya_theresias_tab_count";

function readTabCount(): number {
  try {
    return parseInt(localStorage.getItem(TAB_COUNT_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function writeTabCount(n: number): void {
  try {
    if (n <= 0) localStorage.removeItem(TAB_COUNT_KEY);
    else localStorage.setItem(TAB_COUNT_KEY, String(n));
  } catch {
    /* localStorage unavailable (private mode / quota) — skip silently */
  }
}

/**
 * Browser-native lifecycle telemetry. Mount once at App root.
 *
 * Fires 4 Theresias events:
 *   browser-native-tab-leave     when document.visibilityState becomes "hidden"
 *   browser-native-tab-return    when document.visibilityState becomes "visible"
 *   browser-native-tab-close     on pagehide when other tabs remain open
 *   browser-native-browser-close on pagehide when this was the last tab
 *
 * Tab-vs-browser distinction is heuristic: a shared localStorage counter
 * (`_zenvoya_theresias_tab_count`) is incremented on mount and decremented on
 * pagehide. When the count is 1 at pagehide, this tab is the last one — fire
 * browser-close; otherwise fire tab-close.
 *
 * Coexists with the existing pagehide / visibilitychange listeners in
 * App.tsx (tab-registration cleanup) and useGlobalEvents.ts (15-min mobile
 * reload). Browsers deliver events to all registered handlers independently;
 * this hook neither reads nor mutates the other handlers' state.
 *
 * bfcache safety: pagehide fires for back/forward-cache transitions too;
 * `event.persisted === true` means the page is NOT actually unloading, so we
 * skip the fire and leave the tab count untouched.
 */
export function useBrowserLifecycleTracking(): void {
  const fireTheresiasEvent = useTheresiasEventBridge();
  const fireRef = useRef(fireTheresiasEvent);
  fireRef.current = fireTheresiasEvent;

  useEffect(() => {
    // Increment counter on mount so subsequent pagehide reads the correct
    // "tabs open at the moment we leave" value.
    writeTabCount(readTabCount() + 1);

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        fireRef.current("browser-native-tab-leave", { signal: "VISIBILITY" });
      } else if (document.visibilityState === "visible") {
        fireRef.current("browser-native-tab-return", { signal: "VISIBILITY" });
      }
    };

    const onPageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return; // bfcache — not truly unloading

      const beforeCount = readTabCount();
      const isLastTab = beforeCount <= 1;

      if (isLastTab) {
        // Spec §F: { detection: "LAST_TAB_REGISTRY" } — diagnoses HOW we
        // concluded this was the last tab (read the localStorage counter
        // and saw count === 1 at pagehide).
        fireRef.current("browser-native-browser-close", {
          detection: "LAST_TAB_REGISTRY",
        });
      } else {
        // Spec §F: { persisted, openTabsRemaining }. `persisted` mirrors
        // PageTransitionEvent.persisted — false here because the bfcache
        // case is early-returned above. `openTabsRemaining` is the
        // post-decrement remaining count (renamed from `remainingTabs`).
        fireRef.current("browser-native-tab-close", {
          persisted: false,
          openTabsRemaining: beforeCount - 1,
        });
      }

      writeTabCount(beforeCount - 1);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
